import { normalizeFormOfEducationValue } from "@/src/constants/AdmissionOffice/Malta/maltaFormOfEducationOptions";
import { ITaskListItem } from "@/src/api/tasksPageRequests";
import { AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import { ITasksTableRow } from "@/src/types/tasksTable";
import { resolveMaltaOfferedCourseLabel } from "@/src/utils/admissionOffice/malta/resolveMaltaOfferedCourseLabel";
import { resolveDisplayDegreeAndCourse } from "@/src/utils/admissionOffice/resolveDisplayDegreeAndCourse";
import { mapTaskApiStatusToUi } from "@/src/utils/tasks/mapTaskStatus";
import {
  parseTaskApplicantId,
  parseTaskApplicantInfo,
  parseTaskNumericId,
  parseTaskUserEmail,
  resolveTaskApiId,
} from "./parseTaskApplicantInfo";
import { normalizeFormOfficeForApi } from "./normalizeFormOfficeForApi";

const pickString = (...values: (string | undefined | null)[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

export const resolveTaskFormOffice = (
  rawTask?: ITaskListItem,
  uiRow?: ITasksTableRow,
  fallbackOffice = "ALL",
): string => {
  const applicantInfo = parseTaskApplicantInfo(rawTask?.applicant_info);
  const candidates = [
    applicantInfo.form_office,
    uiRow?.form_office,
    uiRow?.office,
    fallbackOffice !== "ALL" ? fallbackOffice : undefined,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeFormOfficeForApi(candidate);
    if (normalized) return normalized;
  }

  return "";
};

export interface BuildAdmissionPrefillParams {
  rowId: number;
  uiRow?: ITasksTableRow;
  rawTask?: ITaskListItem;
  taskId?: string;
  applicantUserInfo?: {
    fullname?: string;
    fullnameNat?: string;
    dateOfBirth?: string;
    nationality?: string;
    form_office?: string;
    levelEducation?: string;
  };
}

export const buildAdmissionPrefillPayloadFromTask = ({
  rowId,
  uiRow,
  rawTask,
  taskId,
  applicantUserInfo,
}: BuildAdmissionPrefillParams): AdmissionTaskPrefillPayload | null => {
  if (!uiRow && !rawTask) return null;

  const applicantInfo = parseTaskApplicantInfo(rawTask?.applicant_info);
  const applicantId =
    parseTaskApplicantId(applicantInfo, rawTask?.applicant_id) || uiRow?.student_id || 0;
  const resolvedTaskId = resolveTaskApiId(rawTask, taskId, rowId);
  const rawSelectedCourse = pickString(rawTask?.selected_course, uiRow?.selected_course, uiRow?.course);
  const rawDegree = pickString(
    applicantInfo.level_education,
    applicantInfo.levelEducation,
    uiRow?.level_education,
    applicantUserInfo?.levelEducation,
  );
  const { degree, course: selectedCourse } = resolveDisplayDegreeAndCourse(rawSelectedCourse, rawDegree);
  const formOffice = pickString(applicantInfo.form_office, applicantUserInfo?.form_office, uiRow?.form_office);
  const category = pickString(rawTask?.category, uiRow?.category);
  const admissionSemester = pickString(rawTask?.admission_semester, uiRow?.admission_semester);
  const attachedFile = pickString(uiRow?.attached_file, rawTask?.attached_file);

  return {
    source: "tasks",
    rowId,
    task_id: resolvedTaskId,
    student_id: applicantId || null,
    applicant_id: applicantId || null,
    fullName: pickString(applicantInfo.fullname, applicantInfo.full_name, uiRow?.full_name, applicantUserInfo?.fullname),
    fullNameNat: pickString(
      applicantInfo.fullname_nat,
      applicantInfo.fullnameNat,
      uiRow?.fullname_nat,
      applicantUserInfo?.fullnameNat,
    ),
    dateOfBirth: pickString(
      applicantInfo.birth_date,
      applicantInfo.dateOfBirth,
      uiRow?.date_of_birth,
      applicantUserInfo?.dateOfBirth,
    ),
    nationality: pickString(applicantInfo.nationality, uiRow?.nationality, applicantUserInfo?.nationality),
    formOfEducation: normalizeFormOfEducationValue(pickString(rawTask?.form_of_education, uiRow?.form_of_education)),
    admissionSemester,
    intake: pickString(rawTask?.intake, uiRow?.intake),
    category,
    course: "",
    degree: rawDegree || degree,
    selectedCourse,
    offeredCourse: resolveMaltaOfferedCourseLabel({
      category,
      admissionSemester,
      selectedCourse,
    }),
    mediumOfInstruction: pickString(rawTask?.medium_of_instruction, uiRow?.medium_of_instruction),
    duration: pickString(rawTask?.duration, uiRow?.duration),
    tuitionFee: pickString(rawTask?.tuition_fee, uiRow?.tuition_fee),
    transfer: pickString(rawTask?.transfer_fee, uiRow?.transfer_fee),
    medicalInsurance: pickString(rawTask?.medical_insurance, uiRow?.medical_insurance),
    residencePermit: pickString(rawTask?.residence_permit, uiRow?.residence_permit),
    otherExpenses: pickString(rawTask?.other_expenses, uiRow?.other_expenses),
    acommodation: pickString(rawTask?.acommodation, uiRow?.acommodation),
    admissionFee: pickString(rawTask?.admission_fee, uiRow?.admission_fee),
    completeOneYear: pickString(rawTask?.complete_one_year, uiRow?.complete_one_year),
    priority: pickString(rawTask?.priority, uiRow?.priority),
    note: pickString(rawTask?.note, uiRow?.note),
    deadlineDate: pickString(rawTask?.deadline_date, uiRow?.deadline_date),
    type: pickString(rawTask?.type, uiRow?.type),
    status: mapTaskApiStatusToUi(uiRow?.status ?? rawTask?.status ?? "", { attached_file: attachedFile }),
    attached_file: attachedFile,
    form_office: formOffice,
    office: resolveTaskFormOffice(rawTask, uiRow),
    unmappedTaskFields: {
      assignee_id: parseTaskNumericId(rawTask?.assignee_id) || uiRow?.assignee_id,
      assignee_email: parseTaskUserEmail(rawTask?.assignee) || uiRow?.assignee_email,
      executor_id: parseTaskNumericId(rawTask?.executor_id) || uiRow?.executor_id,
      executor_email: parseTaskUserEmail(rawTask?.executor) || uiRow?.executor_email,
      form_office: formOffice,
    },
  };
};
